import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  Button,
  FormControl,
  FormLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import InfoIcon from "@mui/icons-material/Info";
import dayjs, { Dayjs } from "dayjs";
import { textStyle } from "../../styles/Style";
import "../../../components/styles/popupStyle.css";
import {
  checkData,
  checkDuplicate,
  deleteItem,
  updateData,
} from "../../actions/actionItems";
import DraggableItemList from "../../subcomponents/DNDComponent";
import { buttonValues, DNDFieldsEnum } from "../../../constants/Types";
import { SpaPropertyEnum } from "../../../constants/NavbarList";
import { ClearButton } from "../../subcomponents/ClearButton";
import { CopyButton } from "../../subcomponents/CopyButton";
import { spaTooltipHeaders } from "../../../constants/TooltipInformation";
import { tooltipComponent } from "../../subcomponents/tooltipComponent";
import AlertComponent from "../../subcomponents/AlertComponent";
import {
  validateString,
  validationLength,
  validationMessages,
  validationType,
} from "../../actions/validations";

const YES_NO = ["Yes", "No"];
const GENDERS = ["Male", "Female", "Non-Binary", "Prefer Not to Say"];
const PHONE_TYPES = ["Mobile", "Office", "Home"];
const EMAIL_TYPES = ["Personal", "Work", "Other"];

const defaultDateOfHire = () => dayjs();

const SpaTherapistSetup = ({
  data,
  fieldValues,
}: {
  data: (value: any) => void;
  fieldValues: any;
}) => {
  const [staffCode, setStaffCode] = useState<string | null>(null);
  const [firstName, setFirstName] = useState<string | null>(null);
  const [lastName, setLastName] = useState<string | null>(null);
  const [gender, setGender] = useState<string>("");
  const [dateOfHire, setDateOfHire] = useState<Dayjs | null>(defaultDateOfHire);
  const [seniorityLevel, setSeniorityLevel] = useState<number | "">("");
  const [aka, setAka] = useState<string | null>(null);
  const [hourlyRate, setHourlyRate] = useState<number | "">("");
  const [displayOnAppointmentGrid, setDisplayOnAppointmentGrid] =
    useState<string>("Yes");
  const [phoneType, setPhoneType] = useState<string>("Mobile");
  const [countryCode, setCountryCode] = useState<number | "">("");
  const [phoneNumber, setPhoneNumber] = useState<string | null>(null);
  const [allowGratuity, setAllowGratuity] = useState<string>("No");
  const [allowServiceCharge, setAllowServiceCharge] = useState<string>("No");
  const [allowCommission, setAllowCommission] = useState<string>("No");
  const [commissionClass, setCommissionClass] = useState<string | null>(null);
  const [emailType, setEmailType] = useState<string>("Personal");
  const [emailAddress, setEmailAddress] = useState<string | null>(null);
  const [editClicked, setEditClicked] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [items, setItems] = useState<any[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [lastUpdatedRecord, setLastUpdatedRecord] = useState<any>(null);
  const [nameError, setNameError] = useState<boolean>(false);
  const [firstNameFieldError, setFirstNameFieldError] =
    useState<boolean>(false);
  const [firstNameFieldErrorMessage, setFirstNameFieldErrorMessage] =
    useState<string>("");
  const [lastNameFieldError, setLastNameFieldError] = useState<boolean>(false);
  const [lastNameFieldErrorMessage, setLastNameFieldErrorMessage] =
    useState<string>("");

  const validateTherapistFields = (): boolean => {
    if (!staffCode?.trim()) {
      setAlertMessage("Staff Code is mandatory");
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }
    setNameError(false);
    if (
      staffCode &&
      !validateString(
        staffCode,
        validationLength.departmentName,
        validationType.CODE,
      )
    ) {
      setNameError(true);
      return false;
    }

    const listForDuplicate = selectedId
      ? items.filter((i: any) => String(i.id) !== String(selectedId))
      : items;
    if (checkDuplicate(listForDuplicate, DNDFieldsEnum.STAFFCODE, staffCode)) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }

    if (!firstName?.trim()) {
      setAlertMessage("First Name is mandatory");
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }
    setFirstNameFieldError(false);
    setFirstNameFieldErrorMessage("");
    if (
      firstName &&
      !validateString(
        firstName,
        validationLength.firstName,
        validationType.NAME,
      )
    ) {
      setFirstNameFieldError(true);
      setFirstNameFieldErrorMessage(validationMessages.firstName);
      return false;
    }

    if (!lastName?.trim()) {
      setAlertMessage("Last Name is mandatory");
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }
    setLastNameFieldError(false);
    setLastNameFieldErrorMessage("");
    if (
      lastName &&
      !validateString(lastName, validationLength.lastName, validationType.NAME)
    ) {
      setLastNameFieldError(true);
      setLastNameFieldErrorMessage(validationMessages.lastName);
      return false;
    }
    if (!gender?.trim() || !GENDERS.includes(gender)) {
      setAlertMessage("Gender is mandatory");
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }

    return true;
  };

  useEffect(() => {
    fieldValues && setItems(fieldValues);
  }, []);

  useEffect(() => {
    data(items);
    setSelectedId(checkData(items, selectedId) ? selectedId : null);
  }, [items]);

  const updateItem = () => {
    if (!validateTherapistFields()) {
      return false;
    }
    setItems((prev) =>
      prev.map((item: any) =>
        item.id === selectedId ? { ...item, ...payload } : item,
      ),
    );
    editItem(null);
    setEditClicked(false);
    setSelectedId(null);
    setAlertMessage(`${SpaPropertyEnum.therapistSetup} updated successfully`);
    setAlertType("success");
    setOpenAlert(true);
  };

  const addItem = () => {
    if (!validateTherapistFields()) {
      return false;
    }
    setItems((prev) =>
      updateData(
        prev?.length
          ? [...prev, { id: prev.length + 1, ...payload }]
          : [{ id: 1, ...payload }],
      ),
    );
    setLastUpdatedRecord(payload);
    editItem(null);
    setEditClicked(false);
  };

  const editItem = (list: any) => {
    list?.id && setSelectedId(list.id);
    if (list != null) {
      setEditClicked(true);
      setStaffCode(list["Staff Code"] ?? null);
      setFirstName(list["First Name"] ?? null);
      setLastName(list["Last Name"] ?? null);
      setGender(list?.Gender ?? "");
      setDateOfHire(
        list["Date of Hire"]
          ? dayjs(list["Date of Hire"])
          : defaultDateOfHire(),
      );
      setSeniorityLevel(
        list["Seniority Level"] !== undefined &&
          list["Seniority Level"] !== null
          ? Number(list["Seniority Level"])
          : "",
      );
      setAka(list?.AKA ?? null);
      setHourlyRate(
        list["Hourly Rate"] !== undefined && list["Hourly Rate"] !== null
          ? Number(list["Hourly Rate"])
          : "",
      );
      setDisplayOnAppointmentGrid(list["Display on Appointment Grid"] ?? "Yes");
      setPhoneType(list["Phone Type"] ?? "Mobile");
      setCountryCode(
        list["Country Code"] !== undefined && list["Country Code"] !== null
          ? Number(list["Country Code"])
          : "",
      );
      setPhoneNumber(
        list["Phone Number"]
          ? String(list["Phone Number"]).replace(/\D/g, "")
          : null,
      );
      setAllowGratuity(list["Allow Gratuity"] ?? "No");
      setAllowServiceCharge(list["Allow Service Charge"] ?? "No");
      setAllowCommission(list["Allow Commission"] ?? "No");
      setCommissionClass(list["Commission Class"] ?? null);
      setEmailType(list["Email Type"] ?? "Personal");
      setEmailAddress(list["Email Address"] ?? null);
    } else {
      setStaffCode(null);
      setFirstName(null);
      setLastName(null);
      setFirstNameFieldError(false);
      setFirstNameFieldErrorMessage("");
      setLastNameFieldError(false);
      setLastNameFieldErrorMessage("");
      setGender("");
      setDateOfHire(defaultDateOfHire());
      setSeniorityLevel("");
      setAka(null);
      setHourlyRate("");
      setDisplayOnAppointmentGrid("Yes");
      setPhoneType("Mobile");
      setCountryCode("");
      setPhoneNumber(null);
      setAllowGratuity("No");
      setAllowServiceCharge("No");
      setAllowCommission("No");
      setCommissionClass(null);
      setEmailType("Personal");
      setEmailAddress(null);
    }
  };

  const deleteItemById = (id: number) => {
    setItems(deleteItem(items, id));
  };

  const payload = {
    "Staff Code": staffCode,
    "First Name": firstName,
    "Last Name": lastName,
    Gender: gender,
    "Date of Hire": (dateOfHire ?? defaultDateOfHire()).format("YYYY-MM-DD"),
    "Seniority Level":
      seniorityLevel === "" ? undefined : Number(seniorityLevel),
    AKA: aka,
    "Hourly Rate": hourlyRate === "" ? undefined : Number(hourlyRate),
    "Display on Appointment Grid": displayOnAppointmentGrid,
    "Phone Type": phoneType,
    "Country Code": countryCode === "" ? undefined : Number(countryCode),
    "Phone Number": phoneNumber,
    "Allow Gratuity": allowGratuity,
    "Allow Service Charge": allowServiceCharge,
    "Allow Commission": allowCommission,
    "Commission Class": commissionClass,
    "Email Type": emailType,
    "Email Address": emailAddress,
  };

  return (
    <Stack direction="column" gap="10px">
      <Paper
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          padding: "20px",
        }}
      >
        <Stack
          direction="row"
          width="100%"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h6" sx={textStyle}>
            Therapist Setup
            <Tooltip
              title={tooltipComponent(spaTooltipHeaders.therapistSetupHeader)}
            >
              <InfoIcon fontSize="small" />
            </Tooltip>
          </Typography>
          <Stack direction="row" gap="10px">
            {editClicked && (
              <ClearButton
                click={() => {
                  editItem(null);
                  setSelectedId(null);
                }}
              />
            )}
            {lastUpdatedRecord && (
              <CopyButton click={() => editItem(lastUpdatedRecord)} />
            )}
          </Stack>
        </Stack>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Stack
            className="form-two-col-row"
            direction="row"
            flexWrap="wrap"
            gap="16px"
          >
            <Stack direction="column" className="form-two-col-field">
              <Stack direction="row">
                <FormLabel>Staff Code *</FormLabel>
                <Tooltip
                  title={tooltipComponent(
                    spaTooltipHeaders.therapistSetupStaffCode,
                  )}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </Stack>{" "}
              <TextField
                variant="outlined"
                size="small"
                value={staffCode ?? ""}
                onChange={(e) => setStaffCode(e.target.value || null)}
                fullWidth
                helperText={
                  nameError
                    ? "Enter a staff code with letters and numbers only; no spaces or special characters."
                    : ""
                }
                error={nameError}
              />
            </Stack>
            <Stack direction="column" className="form-two-col-field">
              <Stack direction="row">
                <FormLabel>First Name *</FormLabel>
                <Tooltip
                  title={tooltipComponent(
                    spaTooltipHeaders.therapistSetupFirstName,
                  )}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </Stack>{" "}
              <TextField
                variant="outlined"
                size="small"
                value={firstName ?? ""}
                onChange={(e) => setFirstName(e.target.value || null)}
                error={firstNameFieldError}
                helperText={
                  firstNameFieldError ? firstNameFieldErrorMessage : ""
                }
                fullWidth
              />
            </Stack>
            <Stack direction="column" className="form-two-col-field">
              <Stack direction="row">
                <FormLabel>Last Name *</FormLabel>
                <Tooltip
                  title={tooltipComponent(
                    spaTooltipHeaders.therapistSetupLastName,
                  )}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </Stack>{" "}
              <TextField
                variant="outlined"
                size="small"
                value={lastName ?? ""}
                onChange={(e) => setLastName(e.target.value || null)}
                error={lastNameFieldError}
                helperText={lastNameFieldError ? lastNameFieldErrorMessage : ""}
                fullWidth
              />
            </Stack>
            <Stack direction="column" className="form-two-col-field">
              <Stack direction="row">
                <FormLabel>Gender *</FormLabel>
                <Tooltip
                  title={tooltipComponent(
                    spaTooltipHeaders.therapistSetupGender,
                  )}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </Stack>
              <FormControl size="small" fullWidth>
                <Select
                  displayEmpty
                  value={gender}
                  onChange={(e) => setGender(e.target.value)}
                  renderValue={(selected) =>
                    selected ? selected : "Select gender"
                  }
                >
                  <MenuItem value="">
                    <em>Select gender</em>
                  </MenuItem>
                  {GENDERS.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
            <Stack direction="column" className="form-two-col-field">
              <Stack direction="row">
                <FormLabel>Date of Hire</FormLabel>
                <Tooltip
                  title={tooltipComponent(
                    spaTooltipHeaders.therapistSetupDateOfHire,
                  )}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </Stack>{" "}
              <DatePicker
                value={dateOfHire}
                onChange={(v) => setDateOfHire(v)}
                slotProps={{ textField: { size: "small", fullWidth: true } }}
              />
            </Stack>
            <Stack direction="column" className="form-two-col-field">
              <Stack direction="row">
                <FormLabel>Seniority Level</FormLabel>
                <Tooltip
                  title={tooltipComponent(
                    spaTooltipHeaders.therapistSetupSeniorityLevel,
                  )}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </Stack>{" "}
              <TextField
                type="number"
                variant="outlined"
                size="small"
                value={seniorityLevel}
                onChange={(e) =>
                  setSeniorityLevel(
                    e.target.value === ""
                      ? ""
                      : parseInt(e.target.value, 10) || "",
                  )
                }
                fullWidth
              />
            </Stack>
            <Stack direction="column" className="form-two-col-field">
              <Stack direction="row">
                <FormLabel>AKA</FormLabel>
                <Tooltip
                  title={tooltipComponent(spaTooltipHeaders.therapistSetupAka)}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </Stack>{" "}
              <TextField
                variant="outlined"
                size="small"
                value={aka ?? ""}
                onChange={(e) => setAka(e.target.value || null)}
                fullWidth
              />
            </Stack>
            <Stack direction="column" className="form-two-col-field">
              <Stack direction="row">
                <FormLabel>Hourly Rate</FormLabel>
                <Tooltip
                  title={tooltipComponent(
                    spaTooltipHeaders.therapistSetupHourlyRate,
                  )}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </Stack>{" "}
              <TextField
                type="number"
                inputProps={{ step: 0.01 }}
                variant="outlined"
                size="small"
                value={hourlyRate}
                onChange={(e) =>
                  setHourlyRate(
                    e.target.value === ""
                      ? ""
                      : parseFloat(e.target.value) || "",
                  )
                }
                fullWidth
              />
            </Stack>
            <Stack direction="column" className="form-two-col-field">
              <Stack direction="row">
                <FormLabel>Display on Appointment Grid</FormLabel>
                <Tooltip
                  title={tooltipComponent(
                    spaTooltipHeaders.therapistSetupDisplayOnAppointmentGrid,
                  )}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </Stack>{" "}
              <Autocomplete
                size="small"
                options={YES_NO}
                value={displayOnAppointmentGrid}
                onChange={(_, v) => setDisplayOnAppointmentGrid(v ?? "Yes")}
                renderInput={(params) => (
                  <TextField {...params} variant="outlined" />
                )}
              />
            </Stack>
            <Stack direction="column" className="form-two-col-field">
              <Stack direction="row">
                <FormLabel>Phone Type</FormLabel>
                <Tooltip
                  title={tooltipComponent(
                    spaTooltipHeaders.therapistSetupPhoneType,
                  )}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </Stack>{" "}
              <Autocomplete
                size="small"
                options={PHONE_TYPES}
                value={phoneType}
                onChange={(_, v) => setPhoneType(v ?? "Mobile")}
                renderInput={(params) => (
                  <TextField {...params} variant="outlined" />
                )}
              />
            </Stack>
            <Stack direction="column" className="form-two-col-field">
              <Stack direction="row">
                <FormLabel>Country Code</FormLabel>
                <Tooltip
                  title={tooltipComponent(
                    spaTooltipHeaders.therapistSetupCountryCode,
                  )}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </Stack>{" "}
              <TextField
                type="number"
                variant="outlined"
                size="small"
                value={countryCode}
                onChange={(e) =>
                  setCountryCode(
                    e.target.value === ""
                      ? ""
                      : parseInt(e.target.value, 10) || "",
                  )
                }
                fullWidth
              />
            </Stack>
            <Stack direction="column" className="form-two-col-field">
              <Stack direction="row">
                <FormLabel>Phone Number</FormLabel>
                <Tooltip
                  title={tooltipComponent(
                    spaTooltipHeaders.therapistSetupPhoneNumber,
                  )}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </Stack>{" "}
              <TextField
                variant="outlined"
                size="small"
                value={phoneNumber ?? ""}
                onChange={(e) => {
                  const v = e.target.value.replace(/\D/g, "");
                  setPhoneNumber(v || null);
                }}
                fullWidth
              />
            </Stack>
            <Stack direction="column" className="form-two-col-field">
              <Stack direction="row">
                <FormLabel>Allow Gratuity</FormLabel>
                <Tooltip
                  title={tooltipComponent(
                    spaTooltipHeaders.therapistSetupAllowGratuity,
                  )}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </Stack>{" "}
              <Autocomplete
                size="small"
                options={YES_NO}
                value={allowGratuity}
                onChange={(_, v) => setAllowGratuity(v ?? "No")}
                renderInput={(params) => (
                  <TextField {...params} variant="outlined" />
                )}
              />
            </Stack>
            <Stack direction="column" className="form-two-col-field">
              <Stack direction="row">
                <FormLabel>Allow Service Charge</FormLabel>
                <Tooltip
                  title={tooltipComponent(
                    spaTooltipHeaders.therapistSetupAllowServiceCharge,
                  )}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </Stack>{" "}
              <Autocomplete
                size="small"
                options={YES_NO}
                value={allowServiceCharge}
                onChange={(_, v) => setAllowServiceCharge(v ?? "No")}
                renderInput={(params) => (
                  <TextField {...params} variant="outlined" />
                )}
              />
            </Stack>
            <Stack direction="column" className="form-two-col-field">
              <Stack direction="row">
                <FormLabel>Allow Commission</FormLabel>
                <Tooltip
                  title={tooltipComponent(
                    spaTooltipHeaders.therapistSetupAllowCommission,
                  )}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </Stack>{" "}
              <Autocomplete
                size="small"
                options={YES_NO}
                value={allowCommission}
                onChange={(_, v) => setAllowCommission(v ?? "No")}
                renderInput={(params) => (
                  <TextField {...params} variant="outlined" />
                )}
              />
            </Stack>
            <Stack direction="column" className="form-two-col-field">
              <Stack direction="row">
                <FormLabel>Commission Class</FormLabel>
                <Tooltip
                  title={tooltipComponent(
                    spaTooltipHeaders.therapistSetupCommissionClass,
                  )}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </Stack>{" "}
              <TextField
                variant="outlined"
                size="small"
                value={commissionClass ?? ""}
                onChange={(e) => setCommissionClass(e.target.value || null)}
                fullWidth
              />
            </Stack>
            <Stack direction="column" className="form-two-col-field">
              <Stack direction="row">
                <FormLabel>Email Type</FormLabel>
                <Tooltip
                  title={tooltipComponent(
                    spaTooltipHeaders.therapistSetupEmailType,
                  )}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </Stack>{" "}
              <Autocomplete
                size="small"
                options={EMAIL_TYPES}
                value={emailType}
                onChange={(_, v) => setEmailType(v ?? "Personal")}
                renderInput={(params) => (
                  <TextField {...params} variant="outlined" />
                )}
              />
            </Stack>
            <Stack direction="column" className="form-two-col-field">
              <Stack direction="row">
                <FormLabel>Email Address</FormLabel>
                <Tooltip
                  title={tooltipComponent(
                    spaTooltipHeaders.therapistSetupEmailAddress,
                  )}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </Stack>{" "}
              <TextField
                type="email"
                variant="outlined"
                size="small"
                value={emailAddress ?? ""}
                onChange={(e) => setEmailAddress(e.target.value || null)}
                fullWidth
              />
            </Stack>
          </Stack>
        </LocalizationProvider>
        <Stack direction="row" justifyContent="flex-end" mt={2}>
          <Button
            variant="contained"
            sx={{
              cursor: "pointer",
              border: "1px solid gray",
              borderRadius: "10px",
              background: "darkgreen",
              color: "white",
            }}
            onClick={() => (selectedId ? updateItem() : addItem())}
          >
            {selectedId ? buttonValues.UPDATE : buttonValues.ADD} Therapist
          </Button>
        </Stack>
        <Stack direction="column" gap="10px" mt={4}>
          <Typography variant="body2" sx={{ color: "red", fontWeight: 800 }}>
            Do not use sample data. Those are for demo purposes only!
          </Typography>
          <Typography variant="h6">Therapists Available</Typography>
          {items ? (
            <DraggableItemList
              itemData={items}
              onOrderUpdate={(order) => setItems(order)}
              onEdit={editItem}
              onDelete={deleteItemById}
              code="Staff Code"
              name="Last Name"
            />
          ) : (
            <Typography variant="body2">No Therapists Available</Typography>
          )}
        </Stack>
      </Paper>
      <AlertComponent
        message={alertMessage}
        type={alertType}
        alertOpen={openAlert}
        closeAlert={() => setOpenAlert(false)}
      />
    </Stack>
  );
};

export default SpaTherapistSetup;
